import { HouseholdWording } from "./householdWording";
import { TvPosition } from "./nativeApp";

// Household settings for the /home tablet. They live on the device only; there are no accounts.

export type AudioChoice = "auto" | "ask" | "off";

export interface HomeMasjid {
  id: number;
  name: string;
  town?: string;
  // Live audio provider from the masjid index: masjid247, emasjidlive, mymasjid or mixlr.
  audio?: string;
}

export interface HomeSettings {
  // Up to MAX_FAVOURITES; the first is the home masjid.
  favourites: HomeMasjid[];
  audio: Record<string, AudioChoice>;
  wording?: HouseholdWording;
  azan?: AzanSettings;
  // "simple": prayer times only, with posters behind the Posters button. Missing means simple.
  // "next": just the next salah, like the Masjid Clock.
  screenStyle?: "notices" | "simple" | "next";
  // A favourite's own masjid screen (one its masjid ticked for homes), by masjid id. A favourite
  // without one uses screenStyle.
  masjidScreens?: Record<string, number>;
  // App 1.6+ on a TV fixed to the wall on its side. Missing means normal.
  tvPosition?: TvPosition;
  // "mono": black on white, like the Masjid Clock. Missing means colour.
  look?: "colour" | "mono";
  // App 1.2+, with the screen switched off: switch it on this many minutes before each begins time
  // (sunrise and Sehri end count) and each jama'ah, until 5 minutes after. 0 or missing: not.
  wake?: { begins: number; jamaah: number };
}

// Azan alerts for the home masjid, like the clock's buzzer but with an adhan.
export interface AzanSettings {
  atStart: boolean;
  beforeJamaah: boolean;
  minutesBefore: number;
  voice: string;
  volume: number;
  // "short": the first 30 seconds. Missing means the full azan.
  length?: "full" | "short";
}

export const MAX_FAVOURITES = 4;

const STORAGE_KEY = "m247-home-settings";

export const loadHomeSettings = (): HomeSettings | null => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return saved?.favourites?.length ? { audio: {}, ...saved } : null;
  } catch {
    return null;
  }
};

export const saveHomeSettings = (settings: HomeSettings | null): void => {
  try {
    if (settings) localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode or full storage: the tablet still works for this session.
  }
};

// Partner services whose player the app starts for the household, by pressing play in their own
// player. On for the trial (user, 2 Nov 2026) while we ask both for their OK. Mixlr isn't here:
// the app can't press its player's button.
export const AUTO_PLAY_PARTNERS: string[] = ["emasjidlive", "mymasjid"];

// Only app 1.1 and later can press play (MainActivity sets the flag); browsers and app 1.0 can't.
export const partnerStartsItself = (provider?: string): boolean =>
  !!provider &&
  AUTO_PLAY_PARTNERS.includes(provider) &&
  !!(window as Window & { m247PressesPartnerPlay?: boolean }).m247PressesPartnerPlay;

// Only the home masjid may start by itself, so audio never jumps between masjids: its own Masjid
// 24/7 stream, or a partner's that has agreed.
export const canAutoPlay = (settings: HomeSettings, masjid: HomeMasjid): boolean =>
  settings.favourites[0]?.id === masjid.id &&
  (masjid.audio === "masjid247" || partnerStartsItself(masjid.audio));

export const audioChoiceFor = (settings: HomeSettings, masjid: HomeMasjid): AudioChoice => {
  const choice = settings.audio[masjid.id] ?? (masjid.audio ? "ask" : "off");
  return choice === "auto" && !canAutoPlay(settings, masjid) ? "ask" : choice;
};

// What setup suggests: the start-time azan on, unless the home masjid's broadcast plays by
// itself (it carries the azan).
export const suggestedAzan = (settings: HomeSettings): AzanSettings => ({
  atStart: !settings.favourites[0] || audioChoiceFor(settings, settings.favourites[0]) !== "auto",
  beforeJamaah: false,
  minutesBefore: 10,
  voice: "",
  volume: 0.8,
});

// A tablet set up before azan alerts existed never chose them, so it stays quiet until it does.
export const azanSettingsFor = (settings: HomeSettings): AzanSettings =>
  settings.azan ?? { ...suggestedAzan(settings), atStart: false };

// Without a code: the home masjid's prayer times only, with no azan and nothing playing by itself.
// The household's own choices are kept for when a code is entered.
export const previewSettings = (settings: HomeSettings): HomeSettings => ({
  ...settings,
  favourites: settings.favourites.slice(0, 1),
  screenStyle: settings.screenStyle === "next" ? "next" : "simple",
  masjidScreens: {},
  audio: Object.fromEntries(
    Object.entries(settings.audio).map(([id, choice]) => [id, choice === "auto" ? "ask" : choice]),
  ),
  azan: { ...azanSettingsFor(settings), atStart: false, beforeJamaah: false },
});
